/* eslint-disable parog-web/no-glint-expect-error-directive */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import formatDate from 'ember-intl/helpers/format-date';
import { modifier } from 'ember-modifier';
import AddButton from 'parog-web/components/jes-history-table/add-button';
import JesHistoryTableColumn from 'parog-web/components/jes-history-table/column';
import Row from 'parog-web/components/jes-history-table/row';
import incrementalList from 'parog-web/helpers/incremental-list';
import type JournalEventModel from 'parog-web/models/journal-event';
import type Patient from 'parog-web/models/patient';
import ElementRef from 'parog-web/utils/element-ref';

interface Signature {
  Element: HTMLTableElement;
  Args: {
    patient: Patient;
    /** If present, the JesHistoryTable will use the incrementalList helper to incrementally render its rows */
    incrementalListChunk?: Parameters<typeof incrementalList>[1];
    shouldHideButton?: boolean;
  };
  Blocks: { addLabel: []; date: [Date]; noData: [] };
}

/** CellData represents a JournalEventModel[] and has a single
 * `addJe` method to provide a more domain specific API
 * */
class CellData extends Array {
  /** pushes the JE inside the CellData */
  addJe(je) {
    this.push(je);
  }
}

/** RowData represents a CellData[] and has a single
 * `addJeInCell` method to provide a more domain specific API
 * */
export class RowData extends Array {
  /** Initializes a CellData if necessary and adds the JE to it */
  addJeInCell(cellIndex: number, je: JournalEventModel) {
    this[cellIndex] ??= new CellData();
    this[cellIndex].addJe(je);
  }
}

type Timestamp = number & { __brand: 'Timestamp' };

export default class JesHistoryTableComponent extends Component<Signature> {
  @tracked columns: JesHistoryTableColumn[] = [];
  @tracked data = new Map<Timestamp, RowData>();

  dropdownMenu = new ElementRef();

  get dates() {
    return [...this.data.keys()]
      .sort()
      .reverse()
      .map(timestamp => new Date(timestamp));
  }

  /** Returns the chunk size to use for the incrementalList
   *
   * - Uses first the argument @incrementalListChunk if present
   * - Defaults to 20 otherwise
   *
   * If the table contains less than 20 rows, then the number of rows will be used
   * */
  get incrementalListChunk() {
    if (this.args.incrementalListChunk) return this.args.incrementalListChunk;

    const defaultChunkSize = 20;

    // In case we have less rows than defaultChunkSize, then let's use the number of rows
    return this.data.size < defaultChunkSize
      ? this.data.size
      : defaultChunkSize;
  }

  get patient() {
    return this.args.patient;
  }

  #columns: JesHistoryTableColumn[] = [];
  #data = new Map<Timestamp, RowData>();

  /** This method is called every time a Column is instantiated
   *
   * The purpose of this method is to build the data that will be displayed inside the JesHistoryTable
   */
  addColumn = column => {
    // First, we find the index of the column we are dealing with
    const columnIndex = this.#columns.length;

    // then we loop on the JEs it represents
    column.jes.forEach(je => {
      /* We get the occurredOn of the JE as a Timestamp
      and check if we already have a Row for this Timestamp
      If we don't, we initialize the RowData */
      const ts = +je.occurredOn as Timestamp;
      const rowData = this.#data.get(ts) ?? new RowData(columnIndex);

      // We then add the JE at the rown and update this rowData in the JesHistoryTable data
      rowData.addJeInCell(columnIndex, je);
      this.#data.set(ts, rowData);
    });

    // When we are done, we mark this column as processed
    this.#columns.push(column);
  };

  rowDataForDate = (date: Date) => this.data.get(+date as Timestamp) as RowData;

  /**
   * This modifier makes sure the column values are always up-to-date when
   * we insert the rows in the DOM with the incrementalList
   *
   * It is mounted on reach <Row> in the table, but does nothing unless we are
   * at the end of an incremental-list batch
   * */
  setupColumns = modifier((row: HTMLTableRowElement) => {
    /* The last row of the table has been inserted in the DOM,
    it is now time to update the column values for the last time */
    if (row.rowIndex == this.data.size) {
      this.columns.forEach(column => column.setupValues());
      return;
    }

    // Do nothing unless the row is the last one in the incremental list batch
    const isLastBatchRow = row.rowIndex % this.incrementalListChunk == 0;
    if (!isLastBatchRow) return;

    /* The last row of the batch has been inserted in the DOM,
    it is now time to update the column values */
    this.columns.forEach(column => column.setupValues());
  });

  /**
   * This modifier is called when the <table> is inserted in the DOM
   *
   * We can now set the tracked this.data with the data we constucted with addColumn
   * and update the columns array
   *
   * Thanks to this, the <Row> elements will be inserted in the DOM
   * */
  setupData = modifier(() => {
    this.data = this.#data;
    this.columns = this.#columns;
  });

  /* prettier-ignore */
  <template>
    {{! @glint-expect-error }}
    {{yield (component JesHistoryTableColumn table=this onInit=this.addColumn) to='columns' }}

    <div class='flex flex-col' data-test-jes-history-table>
      {{#unless @shouldHideButton}}
        <div class='items-end'>
          {{! @glint-expect-error }}
          <AddButton @table={{this}}>
            {{#if (has-block 'addLabel')}}
              {{yield to='addLabel'}}
            {{else}}
              <i class='plus icon'></i>
              Ajouter une mesure
            {{/if}}
          </AddButton>
        </div>
      {{/unless}}

      <div class='mt-[1em] overflow-x-scroll'>
        <table class='ui table' ...attributes {{this.setupData}}>
          <thead>
            <tr>
              <th>Date</th>
              {{#each this.columns as |column|}}
                <th {{column.labelCell.register}}>
                  {{! content will be replaced by an in-element call }}
                </th>
              {{/each}}
            </tr>
          </thead>

          <tbody>
            {{#each (incrementalList this.dates this.incrementalListChunk) as |date index|}}
              <Row
                @table={{this}}
                @data={{this.rowDataForDate date}}
                data-test-history-table-row={{index}}
                {{this.setupColumns}}
              >
                <:date>
                  {{#if (has-block 'date')}}
                    {{yield date to='date'}}
                  {{else}}
                    {{formatDate date}}
                  {{/if}}
                </:date>
              </Row>
            {{else}}
              {{! @glint-expect-error }}
              <tr colspan={{this.columns.length}}>
                <td>
                  {{#if (has-block 'noData')}}
                    {{yield to='noData'}}
                  {{else}}
                    Aucune valeur enregistrée.
                  {{/if}}
                </td>
              </tr>
            {{/each}}
          </tbody>

        </table>
      </div>
    </div>
  </template>
}
